import { useEffect, useRef } from "react";
import { usePoll, POLL_PRIORITY } from "./usePoll";

// Deploys are announced by build/version.json (stamped by scripts/write-version.js).
// A tab remembers the buildId it saw at startup and force-reloads when a poll sees
// a different one, so stale tabs pick up a release before their lazy chunks 404
// (UP-3054). The poll rides the shared scheduler: paused while the tab is hidden,
// resumed on return — exactly the moment a stale tab would otherwise break.
async function fetchBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?ts=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const contentType = res.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) return null;
    const data = await res.json();
    return typeof data?.buildId === "string" ? data.buildId : null;
  } catch {
    // silent: version probe failing offline/in dev is a normal outcome
    return null;
  }
}

export function useAppVersionRefresh(): void {
  const baselineRef = useRef<string | null>(null);

  useEffect(() => {
    fetchBuildId().then((id) => {
      if (baselineRef.current === null) baselineRef.current = id;
    });
  }, []);

  usePoll(
    "appVersion",
    async () => {
      const current = await fetchBuildId();
      if (!current) return;
      if (baselineRef.current === null) {
        baselineRef.current = current;
        return;
      }
      if (current !== baselineRef.current) {
        window.location.reload();
      }
    },
    { priority: POLL_PRIORITY.LOW }
  );
}
